/**
 * The output window: a stack of typed cards with back-navigation.
 *
 * This is where explicitness lives. The chat column reads like prose; the
 * structure — per-claim status, tiers, the actual lexicon entry — lives here,
 * one click away. That split is the design's "implicit prose, explicit
 * substrate": a reader gets a natural answer, and the evidence is always one
 * gesture from it rather than cluttering it.
 */

import type { JSX } from 'react'

import { useContext, useEffect, useState } from 'react'
import type { AnswerSummary, LexiconResult, Passage, VersionPassage } from './api'
import type { Account } from './account'
import { ActiveVersionsContext } from './activeVersions'
import { getLexicon, getPassage } from './api'
import { EvidenceCard } from './evidence'
import { describeFailure } from '@shared/user-errors.js'
import { t } from './i18n'
import {
  FindingBySlugCard,
  FindingCard,
  FindingEditorCard,
  FindingsCard,
  ImportedCard,
  PackEditorCard,
  PacksCard,
  ReviewCard,
  ThemeEditorCard,
  ThemesCard,
  ThreadEditorCard,
  ThreadsCard,
} from './workspace'

/**
 * Every card is a plain value.
 *
 * Never a closure: the stack has to stay serialisable, and a callback captured
 * inside a card couples navigation to whoever happened to push it. Anything a
 * card needs from React state arrives as a prop on the window instead — which
 * is why `corpus`, `account` and the origins are passed down rather than read
 * from a context somewhere below.
 */
export type Card =
  | { kind: 'passage'; reference: string }
  // The evidence card carries the whole exchange, not just the claims: a
  // finding is the question, the answer and its evidence together, and the
  // card is where the reader decides to keep it.
  | { kind: 'evidence'; question: string; prose: string; summary: AnswerSummary }
  | { kind: 'lexicon'; word: string }
  // The personal corpus, browsed through the same stack as everything else:
  // a finding is a passage-shaped thing and belongs where passages live.
  | { kind: 'findings' }
  | { kind: 'finding'; id: number }
  // An edge carries an id, not a row id, so following one is its own card.
  | { kind: 'findingBySlug'; slug: string }
  | { kind: 'findingEditor'; id: number | null }
  | { kind: 'imported'; author: string }
  | { kind: 'review' }
  | { kind: 'themes' }
  | { kind: 'themeEditor'; id: number | null }
  | { kind: 'threads' }
  | { kind: 'threadEditor'; id: number | null }
  | { kind: 'packs' }
  | { kind: 'packEditor'; id: number | null }

const RIGHT_TO_LEFT = new Set(['he', 'arc', 'ar', 'fa', 'ur'])

/**
 * Everything a card may need from the window, handed down rather than captured.
 *
 * This is the "cards are data, never closures" rule made concrete: the stack holds plain
 * values, and what a card needs from React state arrives here.
 */
interface CardContext {
  account: Account | null
  corpus: string | null
  rootOrigin: string
  /** Whether the Article 9 dialog has been answered; every editor needs it. */
  consented: boolean
  onAccountChange: (account: Account | null) => void
  onStudyChanged: () => void
  onBack: () => void
  onOpen: (card: Card) => void
}

/**
 * One renderer per card kind, and the mapped type is the point: adding a variant to `Card`
 * without adding it here is a compile error. The chain of `top.kind === '…' &&` this
 * replaces could not say that — a forgotten kind rendered nothing at all, silently, which
 * looks exactly like a card that failed to load.
 */
type CardRenderers = {
  [K in Card['kind']]: (card: Extract<Card, { kind: K }>, context: CardContext) => JSX.Element
}

const CARD_RENDERERS: CardRenderers = {
  passage: (card) => <PassageCard reference={card.reference} />,
  evidence: (card, { account, onAccountChange, onOpen }) => (
    <EvidenceCard
      question={card.question}
      prose={card.prose}
      summary={card.summary}
      account={account}
      onAccountChange={onAccountChange}
      onOpen={onOpen}
    />
  ),
  lexicon: (card, { onOpen }) => <LexiconCard word={card.word} onOpen={onOpen} />,
  findings: (_card, { corpus, onOpen }) => <FindingsCard corpus={corpus} onOpen={onOpen} />,
  finding: (card, { account, onOpen, onStudyChanged }) => (
    <FindingCard
      id={card.id}
      account={account}
      onOpen={onOpen}
      onStudyChanged={onStudyChanged}
    />
  ),
  findingBySlug: (card, { corpus, account, onOpen, onStudyChanged }) => (
    <FindingBySlugCard
      slug={card.slug}
      corpus={corpus}
      account={account}
      onOpen={onOpen}
      onStudyChanged={onStudyChanged}
    />
  ),
  findingEditor: (card, { corpus, account, consented, onOpen, onBack }) => (
    <FindingEditorCard
      id={card.id}
      corpus={corpus}
      account={account}
      consented={consented}
      onOpen={onOpen}
      onBack={onBack}
    />
  ),
  imported: (card, { corpus, onOpen }) => (
    <ImportedCard author={card.author} corpus={corpus} onOpen={onOpen} />
  ),
  review: (_card, { corpus, onOpen, onStudyChanged }) => (
    <ReviewCard corpus={corpus} onOpen={onOpen} onStudyChanged={onStudyChanged} />
  ),
  themes: (_card, { corpus, onOpen }) => <ThemesCard corpus={corpus} onOpen={onOpen} />,
  themeEditor: (card, { corpus, consented, onBack }) => (
    <ThemeEditorCard id={card.id} corpus={corpus} consented={consented} onBack={onBack} />
  ),
  threads: (_card, { corpus, onOpen }) => <ThreadsCard corpus={corpus} onOpen={onOpen} />,
  threadEditor: (card, { corpus, consented, onBack }) => (
    <ThreadEditorCard id={card.id} corpus={corpus} consented={consented} onBack={onBack} />
  ),
  packs: (_card, { corpus, rootOrigin, onOpen }) => (
    <PacksCard corpus={corpus} rootOrigin={rootOrigin} onOpen={onOpen} />
  ),
  packEditor: (card, { corpus, consented, onBack }) => (
    <PackEditorCard id={card.id} corpus={corpus} consented={consented} onBack={onBack} />
  ),
}

function renderCard(card: Card, context: CardContext): JSX.Element {
  // The cast is the one thing the mapped type cannot do for us: TypeScript will not prove
  // that `CARD_RENDERERS[card.kind]` and `card` narrow to the same variant together, even
  // though the table's type guarantees exactly that. The exhaustiveness is what matters
  // and it is checked at the table.
  const render = CARD_RENDERERS[card.kind] as (card: Card, context: CardContext) => JSX.Element
  return render(card, context)
}

export function OutputWindow({
  stack,
  account,
  corpus,
  rootOrigin,
  onAccountChange,
  onStudyChanged,
  onBack,
  onClose,
  onOpen,
}: {
  stack: Card[]
  account: Account | null
  /** Which corpus this site serves, resolved once and handed down. */
  corpus: string | null
  rootOrigin: string
  onAccountChange: (account: Account | null) => void
  /** Something a card changed that the header shows — the review queue count. */
  onStudyChanged: () => void
  onBack: () => void
  onClose: () => void
  onOpen: (card: Card) => void
}): JSX.Element {
  const [expanded, setExpanded] = useState(false)
  const top = stack.at(-1)
  // Whether the Article 9 dialog has already been answered. Every editor needs
  // it, because the first thing a reader creates is the moment consent would
  // be collected — and a reader who agreed once is never asked again.
  const consented = account?.has_consented_to_findings ?? false

  if (!top) {
    return (
      <aside className="output output--empty">
        <p className="muted">{t('output.empty')}</p>
      </aside>
    )
  }

  return (
    <aside className={expanded ? 'output output--expanded' : 'output'}>
      <OutputBar
        canGoBack={stack.length > 1}
        expanded={expanded}
        onBack={onBack}
        onToggleExpanded={() => setExpanded((value) => !value)}
        onClose={onClose}
      />

      <div className="output__body">
        {renderCard(top, {
          account,
          corpus,
          rootOrigin,
          consented,
          onAccountChange,
          onStudyChanged,
          onBack,
          onOpen,
        })}
      </div>
    </aside>
  )
}

/**
 * The window's own controls: back through the stack, expand to full height, close.
 *
 * The interaction ladder in one row — click opened this card, expand immerses in it. Back
 * is a spacer rather than a hidden button at the bottom of the stack, so the two actions
 * on the right do not shift position as the reader navigates.
 */
function OutputBar({
  canGoBack,
  expanded,
  onBack,
  onToggleExpanded,
  onClose,
}: {
  canGoBack: boolean
  expanded: boolean
  onBack: () => void
  onToggleExpanded: () => void
  onClose: () => void
}): JSX.Element {
  return (
    <header className="output__bar">
      {canGoBack ? (
        <button type="button" onClick={onBack}>
          ← {t('output.back')}
        </button>
      ) : (
        <span />
      )}
      <div className="output__actions">
        <button type="button" onClick={onToggleExpanded}>
          {expanded ? t('output.collapse') : t('output.expand')}
        </button>
        <button type="button" onClick={onClose}>
          {t('output.close')}
        </button>
      </div>
    </header>
  )
}

/** Loads a resource and renders it, with the loading and error states shared. */
function useResource<T>(load: () => Promise<T>, key: string) {
  const [state, setState] = useState<
    { status: 'loading' } | { status: 'ready'; data: T } | { status: 'error'; message: string }
  >({ status: 'loading' })

  useEffect(() => {
    let cancelled = false
    setState({ status: 'loading' })
    load()
      .then((data) => !cancelled && setState({ status: 'ready', data }))
      .catch(
        (error: unknown) =>
          !cancelled && setState({ status: 'error', message: describeFailure(error, t('error.notLoaded')) }),
      )
    return () => {
      cancelled = true
    }
    // The key is what identifies the resource; `load` is a fresh closure each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return state
}

function PassageCard({ reference }: { reference: string }) {
  const active = useContext(ActiveVersionsContext)
  const state = useResource<Passage>(() => getPassage(reference, active), `${reference}|${active?.join(',') ?? ''}`)

  if (state.status === 'loading') return <p className="muted">…</p>
  if (state.status === 'error') return <p className="error">{state.message}</p>

  return (
    <article>
      <h2 className="card__kind">{t('card.passage')}</h2>
      <h3>{state.data.reference}</h3>
      {/* The public page, same origin: a link a reader can keep or send on. */}
      {state.data.page !== null && (
        <p className="passage__page">
          <a href={state.data.page}>{t('card.passagePage')}</a>
        </p>
      )}

      {state.data.versions.map((version) => (
        <section
          key={version.code}
          className="passage__version"
          lang={version.language}
          dir={RIGHT_TO_LEFT.has(version.language) ? 'rtl' : undefined}
        >
          <h4>
            {version.name}
            <span className="tier">{t('tier.label', { ordinal: version.tier })}</span>
          </h4>
          {version.verses.map((verse) => (
            <p key={`${verse.chapter}:${verse.verse}`} className="verse">
              <span className="verse__number">{verse.marker}</span> {verse.text}
            </p>
          ))}
        </section>
      ))}

      <footer className="attribution">
        {state.data.versions.map((version) => (
          <p key={version.code}>
            <strong>{version.code}</strong> — <Attribution version={version} />
          </p>
        ))}
      </footer>
    </article>
  )
}


/**
 * A version's attribution with its CC licence linked, the licence named once.
 *
 * Every CC attribution here already states its licence ("Licensed CC BY-SA
 * 4.0."), so the link goes on that name in place; one that does not gets the
 * name added after it. The static pages do the same (`pipeline/page_shell.py`,
 * `_attribution`), so a version is credited alike on both surfaces.
 */
function Attribution({ version }: { version: VersionPassage }): JSX.Element {
  if (version.licence_url === null) return <>{version.attribution}</>
  const link = (
    <a href={version.licence_url} rel="license">
      {version.licence_name ?? version.licence_url}
    </a>
  )
  const at = version.licence_name === null ? -1 : version.attribution.indexOf(version.licence_name)
  if (version.licence_name === null || at < 0) return <>{version.attribution} ({link})</>
  return (
    <>
      {version.attribution.slice(0, at)}
      {link}
      {version.attribution.slice(at + version.licence_name.length)}
    </>
  )
}


function LexiconCard({ word, onOpen }: { word: string; onOpen: (card: Card) => void }) {
  const state = useResource<LexiconResult>(() => getLexicon(word), word)

  if (state.status === 'loading') return <p className="muted">…</p>
  if (state.status === 'error') return <p className="error">{state.message}</p>

  return (
    <article>
      <h2 className="card__kind">{t('card.lexicon')}</h2>
      {state.data.entries.map((entry) => (
        <section key={entry.key} className="lexicon__entry">
          <h3 lang={entry.language} dir={RIGHT_TO_LEFT.has(entry.language) ? 'rtl' : undefined}>
            {entry.lemma}
          </h3>
          <p className="muted">
            {entry.key} · {entry.transliteration} · {entry.morphology}
            <span className="tier">{t('tier.label', { ordinal: entry.tier })}</span>
          </p>
          {entry.gloss && <p className="lexicon__gloss">{entry.gloss}</p>}
          {entry.entry && (
            <div
              className="lexicon__body"
              // The lexicon ships light markup (<b>, <i>, <br>) from STEPBible.
              dangerouslySetInnerHTML={{ __html: sanitize(entry.entry) }}
            />
          )}
        </section>
      ))}

      {state.data.occurrences.length > 0 && (
        <section>
          <h4>{t('lexicon.occurrences', { count: state.data.occurrence_count })}</h4>
          <p className="claim__refs">
            {state.data.occurrences.map((occurrence) => (
              <button
                key={occurrence.reference}
                type="button"
                className="link"
                onClick={() => onOpen({ kind: 'passage', reference: occurrence.reference })}
              >
                {occurrence.reference}
              </button>
            ))}
          </p>
        </section>
      )}

      <p className="muted caveat">{t('lexicon.caveat')}</p>
    </article>
  )
}

/**
 * Strip everything but the handful of inline tags the lexicon actually uses.
 *
 * The corpus is ours and built from vetted sources, so this is defence in
 * depth rather than a live threat — but the entries are third-party text
 * rendered as HTML, and an allowlist costs four lines.
 */
function sanitize(html: string): string {
  return html
    .replace(/<(?!\/?(b|i|em|strong|br|sup|sub)\b)[^>]*>/gi, '')
    .replace(/ on\w+="[^"]*"/gi, '')
}

